import { t } from "@/lib/lang";
import BillFiles from "@/components/purchase/BillFiles";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Lock, LockOpen, Plus, Settings2, TriangleAlert } from "lucide-react";
import { toast } from "sonner";
import { usePOS } from "@/context/POSContext";
import { PageShell } from "@/components/PageShell";
import { Modal } from "@/components/Modals";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { featureAllowed } from "@/lib/access";
import { MONEY_LINE, inr } from "@/lib/format";
import * as api from "@/lib/registerApi";

/**
 * Register: the daily cash book (owner, 2026-11-02). It replaced the cash shift
 * and shift history on the Staff page: "Daily cash book but we follow a
 * different standard so ... make everything in it customisable."
 *
 * One business day at a time: opening, cash sales, money in and out under the
 * outlet's own heads, supplier payments taken from the drawer, the expected
 * cash, the count and the difference. What it counts and shows is set in
 * Settings → Operations → Register; the heads are edited here.
 */

const addDays = (iso, n) => {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};
const dayLabel = iso => new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short", year: "numeric" });
const num = value => Number(value || 0);

// The sheet's default sections, in its order (Kaidikhana Daily Sheet, 2026-11-02).
export const DEFAULT_SECTIONS = [
  { key: "expense", label: "Expenses", direction_rule: "out", order: 1, moves_drawer: true },
  { key: "staff", label: "Staff salary / advance", direction_rule: "out", order: 2, moves_drawer: true },
  { key: "deposit", label: "Deposits", direction_rule: "in", order: 3, moves_drawer: true },
  { key: "home_out", label: "Home OUT", direction_rule: "out", order: 4, moves_drawer: true },
  { key: "aside", label: "Set aside (back in tomorrow's opening)", direction_rule: "out", order: 5, moves_drawer: true },
  { key: "card", label: "Card machines (not in the drawer)", direction_rule: "none", order: 6, moves_drawer: false },
  { key: "safe", label: "Safe", direction_rule: null, order: 7, moves_drawer: true },
  { key: "cash", label: "Cash", direction_rule: null, order: 8, moves_drawer: true },
  { key: "other", label: "Other", direction_rule: null, order: 9, moves_drawer: true },
];

function Line({ label, hint, value, sign, strong, testId, children }) {
  return (
    <div className={`flex items-center justify-between gap-base px-base py-snug ${strong ? "bg-muted/40" : ""}`} data-testid={testId}>
      <div className="min-w-0">
        <span className={`text-body ${strong ? "font-semibold" : ""}`}>{label}</span>
        {hint && <span className="block text-micro text-muted-foreground">{hint}</span>}
      </div>
      {children || (
        <strong className={`${MONEY_LINE} ${strong ? "font-semibold" : ""} ${sign === "in" ? "text-success" : sign === "out" ? "text-danger" : ""}`}>
          {sign === "out" && num(value) ? "− " : sign === "in" && num(value) ? "+ " : ""}{inr(num(value))}
        </strong>
      )}
    </div>
  );
}

export default function RegisterPage() {
  const { user, token, permissions } = usePOS();
  const canEdit = featureAllowed("register.edit", user, permissions, ["admin", "cashier"].includes(user?.role));
  const canReopen = featureAllowed("register.reopen", user, permissions, user?.role === "admin");
  const canHeads = featureAllowed("register.heads", user, permissions, user?.role === "admin");
  const canPayees = featureAllowed("register.payees", user, permissions, user?.role === "admin");
  const canBuy = featureAllowed("purchase.edit", user, permissions, user?.role === "admin");
  const canGapsReport = featureAllowed("report.billing-bill-gaps", user, permissions, user?.role === "admin");

  const [day, setDay] = useState(null);
  const [today, setToday] = useState(null);
  const [book, setBook] = useState(null);
  const [heads, setHeads] = useState([]);
  const [history, setHistory] = useState([]);
  const [failure, setFailure] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dialog, setDialog] = useState(null);

  // Entry form
  const [headId, setHeadId] = useState("");
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [party, setParty] = useState("");
  // C2: a salary/advance names a person from the staff list; a salary can recover part of an advance.
  const [staffId, setStaffId] = useState("");
  const [recover, setRecover] = useState("");
  // C6: the party booking this money is for (optional).
  const [eventId, setEventId] = useState("");
  const [cashier, setCashier] = useState("");
  // Count form
  const [pieces, setPieces] = useState({});
  // C1: one total is a count on its own; the notes-and-coins grid is optional.
  const [showPieces, setShowPieces] = useState(false);
  const [countTotal, setCountTotal] = useState("");
  const [opening, setOpening] = useState("");
  const [openingReason, setOpeningReason] = useState("");
  const [notes, setNotes] = useState("");
  const [sections, setSections] = useState(DEFAULT_SECTIONS);

  const sectionMap = useMemo(() => {
    const map = {};
    for (const s of sections) {
      map[s.key] = s;
    }
    return map;
  }, [sections]);

  const sectionLabel = useCallback(key => t(sectionMap[key]?.label) || (key ? key.charAt(0).toUpperCase() + key.slice(1) : "Other"), [sectionMap]);

  const show = useCallback(next => {
    setBook(next);
    setToday(next.options.today);
    setPieces(next.denominations || {});
    setShowPieces(Boolean(next.denominations && Object.keys(next.denominations).length));
    setCountTotal(next.counted == null ? "" : String(num(next.counted)));
    setOpening(next.opening == null ? "" : String(num(next.opening)));
    setOpeningReason(next.opening_reason || "");
    setNotes(next.notes || "");
    setCashier(next.cashier_name || "");
  }, []);

  // Only the latest read may draw: flipping days fast must not leave day A on
  // screen while the page posts to day B.
  const latest = useRef(0);
  const load = useCallback(async target => {
    setFailure(null);
    const ticket = ++latest.current;
    try {
      // The first read is the business day now running; later ones the chosen day.
      const next = target ? await api.readDay(target, token) : await api.readToday(token);
      if (ticket !== latest.current) return;
      show(next);
      setDay(next.business_date);
    } catch (error) {
      if (ticket !== latest.current) return;
      setFailure(api.failureText(error, "The register could not be read from the server."));
    }
  }, [token, show]);

  const loadSide = useCallback(async () => {
    try {
      const [h, days, secs] = await Promise.all([
        api.listHeads(token),
        api.listDays(token),
        api.listSections(token).catch(() => DEFAULT_SECTIONS),
      ]);
      setHeads(h);
      setHistory(days);
      if (secs && secs.length > 0) {
        setSections(secs);
      }
    } catch (error) {
      setFailure(api.failureText(error, "The register heads could not be read."));
    }
  }, [token]);

  useEffect(() => { if (token) { load(null); loadSide(); } }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  const go = target => { setDay(target); load(target); };

  const run = async (work, success) => {
    setBusy(true);
    try {
      const next = await work();
      if (next?.business_date) show(next);
      if (success) toast.success(success);
      loadSide();
      return true;
    } catch (error) {
      toast.error(api.failureText(error, "The register did not save. Nothing changed."));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const closed = Boolean(book?.closed_at);
  const editable = canEdit && !closed;
  // A closed day still takes the sheet's photo (it changes no figure); the server keeps its old ones.
  const canEditPhotos = canEdit;
  const activeHeads = heads.filter(h => h.is_active);
  const denominations = useMemo(() => book?.options?.denominations || [], [book]);
  const piecesTotal = useMemo(
    () => denominations.reduce((sum, face) => sum + face * num(pieces[String(face)]), 0),
    [denominations, pieces]);

  const pickedHead = heads.find(h => String(h.id) === headId);
  const staffHead = pickedHead?.kind === "staff";
  const staffList = book?.staff || [];
  const pickedStaff = staffList.find(p => String(p.id) === staffId);
  const submitEntry = async () => {
    if (!headId || !(num(amount) > 0)) { toast.error("Pick a head and an amount"); return; }
    if (staffHead && !staffId) { toast.error(`Pick who, for ${pickedHead.name}`); return; }
    if (!staffHead && pickedHead?.needs_name && !party.trim()) { toast.error(`Say who, for ${pickedHead.name}`); return; }
    const body = { head_id: Number(headId), amount: num(amount).toFixed(2),
      ...(note.trim() ? { note: note.trim() } : {}),
      ...(staffHead ? { staff_user_id: Number(staffId) } : party.trim() ? { party: party.trim() } : {}),
      ...(staffHead && !pickedHead.is_advance && num(recover) > 0 ? { advance_recovered: num(recover).toFixed(2) } : {}),
      ...(eventId ? { event_id: Number(eventId) } : {}) };
    const ok = await run(() => api.addEntry(day, body, token), "Entry added");
    if (ok) { setAmount(""); setNote(""); setParty(""); setStaffId(""); setRecover(""); setEventId(""); }
  };

  const piecesEntered = Object.values(pieces).some(n => num(n) > 0);
  const saveCount = () => {
    // Pieces and a typed total, when both are given, go together: the server refuses a mismatch by name.
    const body = {};
    if (showPieces && piecesEntered) {
      body.denominations = Object.fromEntries(Object.entries(pieces).filter(([, n]) => num(n) > 0).map(([f, n]) => [f, Math.trunc(num(n))]));
      if (countTotal !== "") body.counted = num(countTotal).toFixed(2);
    } else {
      body.counted = countTotal === "" ? null : num(countTotal).toFixed(2);
    }
    return run(() => api.saveDay(day, { ...body, notes }, token), "Count saved");
  };

  const sign = value => (num(value) > 0 ? "text-success" : num(value) < 0 ? "text-danger" : "");

  const actions = (
    <div className="flex flex-wrap items-center gap-snug">
      <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" disabled={!day} onClick={() => go(addDays(day, -1))} aria-label="Previous day" data-testid="register-prev-day"><ChevronLeft className="h-4 w-4"/></Button>
      <Input type="date" className="h-9 w-auto max-w-[8.5rem] sm:max-w-none sm:w-[10.5rem]" value={day || ""} max={today || undefined}
        onChange={e => e.target.value && go(e.target.value)} data-testid="register-day-input"/>
      <Button variant="outline" size="icon" className="h-9 w-9 shrink-0" disabled={!day || (today && day >= today)} onClick={() => go(addDays(day, 1))} aria-label="Next day" data-testid="register-next-day"><ChevronRight className="h-4 w-4"/></Button>
      {canHeads && <Button variant="outline" className="h-9 shrink-0" onClick={() => setDialog({ kind: "heads" })} data-testid="register-heads-button"><Settings2 className="h-4 w-4 mr-snug"/>Heads</Button>}
    </div>
  );

  return (
    <PageShell title={day ? dayLabel(day) : "Register"} actions={actions} testId="register-page">
      {failure && (
        <div className="bg-destructive/10 text-destructive border border-destructive/20 p-base rounded-lg flex items-start gap-snug" role="alert" data-testid="register-error">
          <TriangleAlert className="w-4 h-4 shrink-0 mt-hair"/><span className="text-body font-semibold">{failure}</span>
          <Button variant="outline" size="sm" className="ml-auto" onClick={() => load(day)}>Try again</Button>
        </div>
      )}
      {!book && !failure && <p className="text-body text-muted-foreground">Reading the register…</p>}
      {book && (
        <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,26rem)_minmax(0,1fr)] gap-loose items-start">
          {/* The day's reckoning */}
          <div className="flex flex-col gap-loose">
            <section className="border rounded-lg bg-card divide-y" data-testid="register-sheet-head">
              <Line label="Sheet no." testId="register-sheet-no"><strong className={MONEY_LINE}>{book.sheet_no}</strong></Line>
              {book.cashier_name && (
                <Line label="Cashier" testId="register-cashier">
                  <strong className="text-body">{book.cashier_name}</strong>
                </Line>
              )}
              {(book.signoffs || []).map(slot => (
                <Line
                  key={slot.slot_id}
                  label={`Sign-off · ${slot.slot_label}`}
                  hint={slot.required_for_close && !slot.signed ? "Required to close day" : null}
                  testId={`register-signoff-${slot.slot_id}`}
                >
                  {slot.signed ? (
                    <span className="text-body font-medium text-success flex items-center gap-tight">
                      ✓ {slot.user_name || "Signed"}
                      {slot.signed_at && (
                        <span className="text-micro text-muted-foreground font-normal ml-1">
                          {new Date(slot.signed_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
                        </span>
                      )}
                    </span>
                  ) : editable ? (
                    <Button
                      size="sm"
                      variant="outline"
                      disabled={busy}
                      onClick={() => setDialog({ kind: "sign", slot })}
                      data-testid={`register-sign-btn-${slot.slot_id}`}
                    >
                      Sign
                    </Button>
                  ) : (
                    <span className="text-body text-muted-foreground">—</span>
                  )}
                </Line>
              ))}
              <Line label="Bill numbers" testId="register-bill-range">
                <span className="flex items-center gap-snug">
                  <strong className={MONEY_LINE}>{book.bill_from ? `${book.bill_from} – ${book.bill_to}` : "—"}</strong>
                  {canGapsReport && (
                    <a className="text-meta text-primary underline-offset-2 hover:underline"
                      href={`/reports?tab=library&report=billing-bill-gaps&from=${day}&to=${day}`} data-testid="register-bill-gaps-link">
                      Gaps &amp; cancelled
                    </a>
                  )}
                </span>
              </Line>
              <Line label="Yesterday counted" hint={book.yesterday_counted ? [book.yesterday_counted.who, book.yesterday_counted.when ? new Date(book.yesterday_counted.when).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : null].filter(Boolean).join(" · ") : null} testId="register-yesterday-counted">
                <strong className={MONEY_LINE}>{book.yesterday_counted?.amount != null ? inr(num(book.yesterday_counted.amount)) : "Not counted"}</strong>
              </Line>
              <Line label="Carried in" value={book.carried_in || book.yesterday_counted?.carried || 0} testId="register-carried-in" />
              <Line label="Today's opening" value={book.opening == null ? null : book.opening} strong testId="register-today-opening">
                <strong className={MONEY_LINE}>{book.opening == null ? "—" : inr(num(book.opening))}</strong>
              </Line>
            </section>
            {book.opening_warning && (
              <p className="text-meta text-destructive bg-destructive/15 border border-destructive/30 rounded-md px-base py-snug font-medium flex items-center gap-snug" role="alert" data-testid="register-opening-warning">
                <TriangleAlert className="w-4 h-4 shrink-0" />
                <span>{book.opening_warning}</span>
              </p>
            )}
            {book.warnings && book.warnings.length > 0 && (
              <div className="flex flex-col gap-tight" data-testid="register-system-warnings">
                {book.warnings.map((w, idx) => (
                  <p key={idx} className="text-meta text-destructive bg-destructive/15 border border-destructive/30 rounded-md px-base py-snug font-medium flex items-center gap-snug" role="alert">
                    <TriangleAlert className="w-4 h-4 shrink-0" />
                    <span>{w}</span>
                  </p>
                ))}
              </div>
            )}
            <datalist id="register-people">{(book.people || []).map(name => <option key={name} value={name}/>)}</datalist>
            <section className="border rounded-lg bg-card divide-y" data-testid="register-summary">
              <Line label="Opening cash" hint={book.opening_source === "saved" ? null : book.opening_source === "none" ? "No earlier day" : book.opening_source === "manual" ? "Type it in" : `From ${book.opening_source}`} testId="register-opening">
                {editable ? (
                  <div className="flex items-center gap-tight flex-wrap">
                    <Input className="h-9 w-28 text-right" inputMode="decimal" placeholder="—" value={opening} onChange={e => setOpening(e.target.value)} data-testid="register-opening-input"/>
                    {num(opening) !== num(book.opening) ? (
                      <>
                        {book.opening_set && (
                          <Input className="h-9 w-36" placeholder="Reason for change" value={openingReason} onChange={e => setOpeningReason(e.target.value)} data-testid="register-opening-reason-input" />
                        )}
                        <Button size="sm" variant="action" disabled={busy} onClick={() => run(() => api.saveDay(day, { opening: opening === "" ? null : num(opening).toFixed(2), reason: openingReason }, token), "Opening saved")} data-testid="register-opening-save">Save</Button>
                      </>
                    ) : book.opening_source === "saved" && book.options.opening_from !== "manual" ? (
                      <Button size="sm" variant="outline" disabled={busy} onClick={() => run(() => api.saveDay(day, { opening: null }, token), "Opening follows the day before")} data-testid="register-opening-auto">Automatic</Button>
                    ) : null}
                  </div>
                ) : <strong className={MONEY_LINE}>{book.opening == null ? "—" : inr(num(book.opening))}</strong>}
              </Line>
              <Line label="Total sale" hint="Bills before discounts and NCs" value={book.total_sale} testId="register-total-sale"/>
              <Line label="Discounts & NC" hint={`${book.discount_count} discounts · ${book.nc_count} NC`} value={num(book.discount_total) + num(book.nc_total)} testId="register-write-offs"/>
              <Line label="Pending bills" hint={`${book.pending_bills.length} not settled`} value={book.pending_total} testId="register-pending"/>
              <Line label="Cash sale" hint={`${book.sales.filter(s => s.is_cash).reduce((n, s) => n + s.bills, 0)} bills paid in cash`} value={book.cash_sales} sign="in" testId="register-cash-sales"/>
              {book.options.cash_tips_in_drawer && <Line label="Cash tips" value={book.cash_tips} sign="in" testId="register-cash-tips"/>}
              {sections.filter(s => s.key !== "card").map(s => {
                const val = num(book.by_section?.[s.key]);
                if (val <= 0) return null;
                const sign = s.direction_rule || (s.key === "deposit" ? "in" : (s.key === "safe" || s.key === "cash" || s.key === "other" ? null : "out"));
                return (
                  <Line key={s.key} label={t(s.label)} value={book.by_section[s.key]} sign={sign} testId={`register-section-${s.key}`}/>
                );
              })}
              {Object.entries(book.by_section || {}).map(([key, amount]) => {
                if (num(amount) <= 0 || key === "card" || sections.some(s => s.key === key)) return null;
                return (
                  <Line key={key} label={key.charAt(0).toUpperCase() + key.slice(1)} value={amount} sign={null} testId={`register-section-${key}`}/>
                );
              })}
              {(book.options.supplier_sources.length > 0) && <Line label="Vendor payments" hint={book.options.supplier_sources.join(", ")} value={book.supplier_out} sign="out" testId="register-supplier-out"/>}
              <Line label="Expected in drawer" value={book.expected} strong testId="register-expected"/>
              <Line label="Counted" testId="register-counted">
                <strong className={MONEY_LINE}>{book.counted == null ? "Not counted" : inr(num(book.counted))}</strong>
              </Line>
              <Line label="Difference" strong testId="register-difference">
                <strong className={`${MONEY_LINE} font-semibold ${sign(book.difference)}`}>
                  {book.difference == null ? "—" : `${num(book.difference) > 0 ? "+ " : num(book.difference) < 0 ? "− " : ""}${inr(Math.abs(num(book.difference)))}`}
                </strong>
              </Line>
            </section>

            {book.changed_since_close != null && (
              <p className="text-meta text-warning-foreground bg-warning/15 border border-warning/30 rounded-md px-base py-snug" data-testid="register-changed-since-close">
                A bill or entry changed after this day was closed: expected cash is now {num(book.changed_since_close) > 0 ? "higher" : "lower"} by {inr(Math.abs(num(book.changed_since_close)))} than when it was closed.
              </p>
            )}

            {book.options.show_other_modes && book.sales.some(s => !s.is_cash) && (
              <section className="border rounded-lg bg-card divide-y" data-testid="register-other-modes">
                <div className="px-base py-snug text-meta font-medium text-muted-foreground">Other payment modes · not in the drawer</div>
                {book.sales.filter(s => !s.is_cash).map(s => (
                  <Line key={s.mode} label={s.mode} hint={`${s.bills} bills`} value={s.amount}/>
                ))}
              </section>
            )}

            {/* The count */}
            <section className="border rounded-lg bg-card p-base flex flex-col gap-snug" data-testid="register-count">
              <div className="flex items-center justify-between">
                <h2 className="text-body font-semibold">Count the drawer</h2>
                {closed && <span className="text-meta text-muted-foreground flex items-center gap-tight"><Lock className="h-3.5 w-3.5"/>Day closed</span>}
              </div>
              <label className="flex flex-col gap-tight">
                <span className="text-micro font-medium text-muted-foreground">Total counted</span>
                <Input className="h-11 text-right" inputMode="decimal" placeholder="Cash in the drawer" disabled={!editable}
                  value={countTotal} onChange={e => setCountTotal(e.target.value)} data-testid="register-count-input"/>
              </label>
              {denominations.length > 0 && (
                <Button variant="ghost" className="h-11 self-start" onClick={() => setShowPieces(v => !v)} aria-expanded={showPieces}
                  data-testid="register-toggle-pieces">{showPieces ? "Hide notes and coins" : "Count notes and coins (optional)"}</Button>
              )}
              {denominations.length > 0 && showPieces && (
                <div className="grid grid-cols-[auto_minmax(0,6rem)_minmax(0,1fr)] items-center gap-x-base gap-y-tight">
                  {denominations.map(face => (
                    <div className="contents" key={face}>
                      <span className="text-body tabular-nums">₹{face} ×</span>
                      <Input className="h-9 text-right" inputMode="numeric" disabled={!editable} value={pieces[String(face)] ?? ""}
                        onChange={e => setPieces(p => ({ ...p, [String(face)]: e.target.value.replace(/[^0-9]/g, "") }))}
                        data-testid={`register-pieces-${face}`}/>
                      <span className={`${MONEY_LINE} text-right`}>{inr(face * num(pieces[String(face)]))}</span>
                    </div>
                  ))}
                  <span className="text-body font-semibold col-span-2">Total</span>
                  <span className={`${MONEY_LINE} font-semibold text-right`} data-testid="register-pieces-total">{inr(piecesTotal)}</span>
                  {countTotal !== "" && piecesEntered && num(countTotal) !== piecesTotal && (
                    <span className="col-span-3 text-meta text-danger" data-testid="register-pieces-mismatch">
                      Notes and coins add up to {inr(piecesTotal)}, not {inr(num(countTotal))}.
                    </span>
                  )}
                </div>
              )}
              <textarea className="w-full min-h-[64px] p-3 text-body rounded-md border bg-card focus:outline-none focus:ring-2 focus:ring-primary/50 disabled:opacity-60"
                placeholder="Notes for the day" disabled={!editable} value={notes} onChange={e => setNotes(e.target.value)} data-testid="register-notes"/>
              <div className="flex flex-wrap justify-end gap-snug">
                {editable && <Button variant="outline" className="h-11" disabled={busy} onClick={saveCount} data-testid="register-save-count">Save count</Button>}
                {editable && <Button variant="action" className="h-11" disabled={busy} onClick={() => setDialog({ kind: "close" })} data-testid="register-close-day"><Lock className="h-4 w-4 mr-snug"/>Close day</Button>}
                {closed && canReopen && <Button variant="outline" className="h-11" disabled={busy} onClick={() => run(() => api.reopenDay(day, token), "Day reopened")} data-testid="register-reopen-day"><LockOpen className="h-4 w-4 mr-snug"/>Reopen day</Button>}
              </div>
            </section>
          </div>

          {/* Entries, supplier payments, past days */}
          <div className="flex flex-col gap-loose min-w-0">
            {editable && (
              <section className="border rounded-lg bg-card p-base grid grid-cols-1 sm:grid-cols-[minmax(0,12rem)_minmax(0,8rem)_repeat(auto-fit,minmax(8rem,1fr))] gap-snug items-end" data-testid="register-entry-form">
                <label className="flex flex-col gap-tight">
                  <span className="text-micro font-medium text-muted-foreground">Head</span>
                  <select className="h-11 px-3 border rounded-md bg-card text-body" value={headId} onChange={e => setHeadId(e.target.value)} data-testid="register-entry-head">
                    <option value="">Pick…</option>
                    {sections.map(s => {
                      const list = activeHeads.filter(h => (h.section || "other") === s.key);
                      return list.length ? (
                        <optgroup key={s.key} label={t(s.label)}>
                          {list.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                        </optgroup>
                      ) : null;
                    })}
                    {(() => {
                      const knownKeys = new Set(sections.map(s => s.key));
                      const extraSections = [...new Set(activeHeads.filter(h => h.section && !knownKeys.has(h.section)).map(h => h.section))];
                      return extraSections.map(secKey => {
                        const list = activeHeads.filter(h => h.section === secKey);
                        return list.length ? (
                          <optgroup key={secKey} label={secKey.charAt(0).toUpperCase() + secKey.slice(1)}>
                            {list.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                          </optgroup>
                        ) : null;
                      });
                    })()}
                  </select>
                </label>
                <label className="flex flex-col gap-tight">
                  <span className="text-micro font-medium text-muted-foreground">Amount</span>
                  <Input className="h-11 text-right" inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} data-testid="register-entry-amount"/>
                </label>
                {staffHead && (
                  <label className="flex flex-col gap-tight">
                    <span className="text-micro font-medium text-muted-foreground">Who</span>
                    <select className="h-11 px-3 border rounded-md bg-card text-body" value={staffId} onChange={e => setStaffId(e.target.value)} data-testid="register-entry-staff">
                      <option value="">Pick…</option>
                      {staffList.map(p => (
                        <option key={p.id} value={p.id}>{p.name}{num(p.advance_outstanding) > 0 ? ` · owes ${inr(num(p.advance_outstanding))}` : ""}</option>
                      ))}
                    </select>
                  </label>
                )}
                {staffHead && !pickedHead.is_advance && num(pickedStaff?.advance_outstanding) > 0 && (
                  <label className="flex flex-col gap-tight">
                    <span className="text-micro font-medium text-muted-foreground">Recover advance (owes {inr(num(pickedStaff.advance_outstanding))})</span>
                    <Input className="h-11 text-right" inputMode="decimal" value={recover} onChange={e => setRecover(e.target.value)} data-testid="register-entry-recover"/>
                  </label>
                )}
                {!staffHead && pickedHead?.needs_name && (
                  <label className="flex flex-col gap-tight">
                    <span className="text-micro font-medium text-muted-foreground">Who</span>
                    <Input className="h-11" list="register-people" value={party} onChange={e => setParty(e.target.value)} data-testid="register-entry-party"/>
                  </label>
                )}
                <label className="flex flex-col gap-tight">
                  <span className="text-micro font-medium text-muted-foreground">Note</span>
                  <Input className="h-11" value={note} onChange={e => setNote(e.target.value)} onKeyDown={e => e.key === "Enter" && submitEntry()} data-testid="register-entry-note"/>
                </label>
                <EventPicker events={book.events || []} value={eventId} onChange={setEventId} token={token} day={day}
                  onCreated={async () => show(await api.readDay(day, token))} testId="register-entry-event"/>
                <Button variant="action" className="h-11" disabled={busy} onClick={submitEntry} data-testid="register-entry-add"><Plus className="h-4 w-4 mr-snug"/>Add</Button>
              </section>
            )}

            <section className="border rounded-lg bg-card divide-y" data-testid="register-entries">
              <div className="px-base py-snug text-meta font-medium text-muted-foreground">Cash in and out</div>
              {book.entries.length === 0 && <p className="px-base py-snug text-body text-muted-foreground">Nothing entered for this day.</p>}
              {[...book.entries].sort((a, b) => {
                const orderA = sectionMap[a.section || "other"]?.order ?? 99;
                const orderB = sectionMap[b.section || "other"]?.order ?? 99;
                return orderA - orderB;
              }).map((e, i, all) => (
                <div key={e.id}>
                  {(i === 0 || (all[i - 1].section || "other") !== (e.section || "other")) && (
                    <div className="px-base pt-snug text-micro font-semibold text-muted-foreground uppercase">{sectionLabel(e.section || "other")}</div>
                  )}
                <div className={`flex items-center gap-base px-base py-snug ${e.voided_at ? "opacity-60" : ""}`} data-testid={`register-entry-${e.id}`}>
                  <div className="min-w-0 flex-1">
                    <span className={`text-body font-medium ${e.voided_at ? "line-through" : ""}`}>{e.head}{e.party ? ` · ${e.party}` : ""}</span>
                    <span className="block text-micro text-muted-foreground truncate">
                      {new Date(e.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}{e.note ? ` · ${e.note}` : ""}{e.voided_at ? ` · struck out: ${e.void_reason}` : ""}
                    </span>
                  </div>
                  <strong className={`${MONEY_LINE} ${e.voided_at ? "line-through" : e.direction === "in" ? "text-success" : "text-danger"}`}>
                    {e.direction === "in" ? "+ " : "− "}{inr(num(e.amount))}
                  </strong>
                  {editable && !e.voided_at && (
                    <Button variant="outline" size="sm" onClick={() => setDialog({ kind: "void", entry: e })} data-testid={`register-entry-void-${e.id}`}>Strike out</Button>
                  )}
                </div>
                </div>
              ))}
            </section>

            {/* The sheet's auto lists */}
            {num(book.card_entered) > 0 || num(book.pos_card) > 0 ? (
              <section className="border rounded-lg bg-card divide-y" data-testid="register-cards">
                <div className="px-base py-snug text-meta font-medium text-muted-foreground">Card machines · typed vs the POS</div>
                <Line label="Typed on the machines" value={book.card_entered}/>
                <Line label="Card on bills (POS)" value={book.pos_card}/>
                {num(book.card_entered) !== num(book.pos_card) && (
                  <p className="px-base py-snug text-meta text-danger" data-testid="register-card-mismatch">
                    Off by {inr(Math.abs(num(book.card_entered) - num(book.pos_card)))}: check the machine slips against the bills.
                  </p>
                )}
              </section>
            ) : null}

            {book.write_offs.length > 0 && (
              <section className="border rounded-lg bg-card" data-testid="register-write-off-list">
                <div className="px-base py-snug text-meta font-medium text-muted-foreground border-b">
                  Discounts & NC · over {book.options.discount_limit_pct}% is an over-discount · average {book.average_discount_pct}%
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-body">
                    <thead><tr className="text-micro text-muted-foreground text-left">
                      <th className="px-base py-tight">Bill</th><th className="px-tight">Reason</th>
                      <th className="px-tight text-right">Amount</th><th className="px-tight text-right">Bill</th>
                      <th className="px-tight text-right">%</th><th className="px-base">Kind</th>
                    </tr></thead>
                    <tbody className="divide-y">
                      {book.write_offs.map(w => (
                        <tr key={w.order_id} data-testid={`register-write-off-${w.order_id}`}>
                          <td className="px-base py-tight tabular-nums">{w.bill_number}</td>
                          <td className="px-tight truncate max-w-[12rem]">{w.reason || "—"}</td>
                          <td className={`px-tight text-right ${MONEY_LINE}`}>{inr(num(w.amount))}</td>
                          <td className={`px-tight text-right ${MONEY_LINE}`}>{inr(num(w.bill_amount))}</td>
                          <td className="px-tight text-right tabular-nums">{w.percent}%</td>
                          <td className={`px-base font-medium ${w.kind === "Over discount" ? "text-danger" : w.kind === "NC" ? "text-warning" : ""}`}>{w.kind}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {book.pending_bills.length > 0 && (
              <section className="border rounded-lg bg-card divide-y" data-testid="register-pending-list">
                <div className="px-base py-snug text-meta font-medium text-muted-foreground">Pending bills · printed, not settled</div>
                {book.pending_bills.map(b => <Line key={b.order_id} label={`Bill ${b.bill_number}`} value={b.amount}/>)}
              </section>
            )}

            {staffList.some(p => num(p.advance_outstanding) > 0) && (
                <section className="border rounded-lg bg-card divide-y" data-testid="register-staff-advances">
                  <div className="px-base py-snug text-meta font-medium text-muted-foreground">Staff advances not yet recovered</div>
                  {staffList.filter(p => num(p.advance_outstanding) > 0).map(p => (
                    <Line key={p.id} label={p.name} value={p.advance_outstanding} testId={`register-staff-owes-${p.id}`}/>
                  ))}
                </section>
              )}
            {(book.events || []).length > 0 && (
              <section className="border rounded-lg bg-card divide-y" data-testid="register-events">
                <div className="px-base py-snug text-meta font-medium text-muted-foreground">Events (party bookings)</div>
                {book.events.map(ev => (
                  <button key={ev.id} type="button" className="w-full text-left px-base py-snug text-body hover:bg-muted/40 min-h-[44px]"
                    onClick={async () => {
                      try { setDialog({ kind: "event", ledger: await api.getEventLedger(ev.id, token) }); }
                      catch (error) { toast.error(api.failureText(error, "The event did not load.")); }
                    }} data-testid={`register-event-${ev.id}`}>
                    {ev.name}{ev.event_date ? ` · ${ev.event_date}` : ""}
                  </button>
                ))}
              </section>
            )}
            <section className="border rounded-lg bg-card p-base" data-testid="register-photos-box">
              <BillFiles files={book.attachments || []} parent={{ day }} token={token} canEdit={canEditPhotos}
                title="Photos of the sheet and slips" emptyText="No photo of today's sheet yet." addedText="Photo added"
                fetchFile={api.fetchDayAttachment} testId="register-photos"
                onUpload={async (_parent, file) => {
                  try { await api.uploadDayAttachment(day, file, token); show(await api.readDay(day, token)); return { ok: true }; }
                  catch (error) { return { ok: false, error: api.failureText(error, "Could not reach the server") }; }
                }}
                onDelete={async id => {
                  try { await api.deleteDayAttachment(id, token); show(await api.readDay(day, token)); return { ok: true }; }
                  catch (error) { return { ok: false, error: api.failureText(error, "Could not reach the server") }; }
                }}/>
            </section>
            {editable && canBuy && (
              <a className="inline-flex items-center h-11 px-base self-start rounded-md border bg-card text-body font-medium hover:bg-muted/40"
                href={`/purchase?enter=bill&paid_from=${encodeURIComponent(book.options?.supplier_sources?.[0] || "Drawer cash")}`}
                data-testid="register-walk-in">Walk-in purchase (paid from the drawer)</a>
            )}
            {editable && (
              <Button variant="outline" className="h-11 self-start" disabled={busy} onClick={() => setDialog({ kind: "payee" })}
                data-testid="register-pay-someone"><Plus className="h-4 w-4 mr-snug"/>Pay someone without a bill</Button>
            )}
            {book.vendor_new_due.length > 0 && (
              <section className="border rounded-lg bg-card divide-y" data-testid="register-vendor-due">
                <div className="px-base py-snug text-meta font-medium text-muted-foreground">Vendor new due · bought today, not yet paid</div>
                {book.vendor_new_due.map(v => (
                  <Line key={v.purchase_order_id} label={v.supplier} value={v.amount} testId={`register-vendor-due-${v.purchase_order_id}`}>
                    {editable ? (
                      <span className="flex items-center gap-snug">
                        <strong className={MONEY_LINE}>{inr(num(v.amount))}</strong>
                        <Button size="sm" variant="action" className="h-9" disabled={busy}
                          onClick={() => setDialog({ kind: "pay", due: v, amount: String(num(v.amount)) })}
                          data-testid={`register-vendor-pay-${v.purchase_order_id}`}>Pay</Button>
                      </span>
                    ) : null}
                  </Line>
                ))}
              </section>
            )}

            {book.supplier_payments.length > 0 && (
              <section className="border rounded-lg bg-card divide-y" data-testid="register-supplier-payments">
                <div className="px-base py-snug text-meta font-medium text-muted-foreground">Supplier payments from the drawer · entered in Purchase</div>
                {book.supplier_payments.map(p => (
                  <Line key={p.id} label={p.supplier} hint={[p.paid_from, p.note].filter(Boolean).join(" · ")} value={p.amount} sign="out"/>
                ))}
              </section>
            )}

            <section className="border rounded-lg bg-card divide-y" data-testid="register-history">
              <div className="px-base py-snug text-meta font-medium text-muted-foreground">Last 30 days</div>
              {history.length === 0 && <p className="px-base py-snug text-body text-muted-foreground">No days saved yet.</p>}
              {history.map(h => (
                <button type="button" key={h.business_date} onClick={() => go(h.business_date)}
                  className={`w-full flex items-center gap-base px-base py-snug text-left hover:bg-muted/50 ${h.business_date === day ? "bg-primary/5" : ""}`}
                  data-testid={`register-history-${h.business_date}`}>
                  <span className="text-body flex-1 min-w-0 truncate">{dayLabel(h.business_date)}</span>
                  <span className={`${MONEY_LINE} text-muted-foreground`}>{inr(num(h.expected))}</span>
                  <span className={`${MONEY_LINE} w-24 text-right ${sign(h.difference)}`}>{h.difference == null ? "—" : inr(num(h.difference))}</span>
                  {h.closed_at ? <Lock className="h-3.5 w-3.5 text-muted-foreground"/> : <span className="w-3.5"/>}
                </button>
              ))}
            </section>
          </div>
        </div>
      )}

      {dialog?.kind === "close" && (
        <Modal title="Close the day?" onClose={() => setDialog(null)} testId="register-close-modal"
          footer={<><Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button variant="action" disabled={busy} onClick={async () => { if (await run(() => api.closeDay(day, token), "Day closed")) setDialog(null); }} data-testid="register-close-confirm">Close day</Button></>}>
          <p className="text-body">
            Expected {inr(num(book.expected))}, counted {book.counted == null ? "nothing yet" : inr(num(book.counted))}.
            {" "}A closed day keeps these figures; entries can no longer be added until it is reopened.
          </p>
          {showPieces && piecesEntered && num(book.counted) !== piecesTotal && (
            <p className="text-body font-semibold text-danger mt-2">The count on screen ({inr(piecesTotal)}) is not saved yet. Save the count first.</p>
          )}
        </Modal>
      )}
      {dialog?.kind === "pay" && (
        <Modal title={`Pay ${dialog.due.supplier} from the drawer`} onClose={() => setDialog(null)} testId="register-pay-modal"
          footer={<><Button variant="outline" onClick={() => setDialog(null)}>Cancel</Button>
            <Button variant="action" disabled={busy || !(num(dialog.amount) > 0)} data-testid="register-pay-confirm"
              onClick={async () => {
                const body = { purchase_order_id: dialog.due.purchase_order_id,
                  ...(num(dialog.amount) !== num(dialog.due.amount) ? { amount: num(dialog.amount).toFixed(2) } : {}),
                  ...(dialog.eventId ? { event_id: Number(dialog.eventId) } : {}) };
                if (await run(() => api.payVendor(day, body, token), `Paid ${dialog.due.supplier}`)) setDialog(null);
              }}>Pay {inr(num(dialog.amount))}</Button></>}>
          <label className="flex flex-col gap-tight">
            <span className="text-micro font-medium text-muted-foreground">Amount (due {inr(num(dialog.due.amount))})</span>
            <Input className="h-11 text-right" inputMode="decimal" value={dialog.amount}
              onChange={e => setDialog(d => ({ ...d, amount: e.target.value }))} data-testid="register-pay-amount"/>
          </label>
          <div className="mt-2">
            <EventPicker events={book.events || []} value={dialog.eventId || ""} onChange={v => setDialog(d => ({ ...d, eventId: v }))}
              token={token} day={day} onCreated={async () => show(await api.readDay(day, token))} testId="register-pay-event"/>
          </div>
          <p className="text-meta text-muted-foreground mt-2">Cash leaves the drawer and the bill's due goes down in Purchases.</p>
        </Modal>
      )}
      {dialog?.kind === "payee" && (
        <PayeeDialog payees={book.payees || []} heads={activeHeads.filter(h => h.direction === "out" && h.kind !== "staff")}
          events={book.events || []} token={token} day={day} onEventCreated={async () => show(await api.readDay(day, token))}
          canCreate={canPayees} busy={busy} onClose={() => setDialog(null)}
          onConfirm={async ({ payeeId, newName, headId: hid, amount: amt, note: nt, eventId: evId }) => {
            const ok = await run(async () => {
              let id = payeeId;
              if (!id) id = (await api.createPayee({ name: newName, default_head_id: hid }, token)).id;
              return api.addEntry(day, { head_id: hid, amount: num(amt).toFixed(2), payee_id: id,
                ...(nt.trim() ? { note: nt.trim() } : {}), ...(evId ? { event_id: Number(evId) } : {}) }, token);
            }, "Paid");
            if (ok) setDialog(null);
          }}/>
      )}
      {dialog?.kind === "event" && (
        <Modal title={dialog.ledger.name} onClose={() => setDialog(null)} testId="register-event-modal"
          footer={<Button variant="outline" onClick={() => setDialog(null)}>Close</Button>}>
          <div className="divide-y">
            {dialog.ledger.lines.map(l => (
              <Line key={`${l.kind}-${l.id}`} label={`${l.what || ""}${l.who ? ` · ${l.who}` : ""}`} hint={l.date} value={l.amount} sign={l.direction}/>
            ))}
            <Line label="In" value={dialog.ledger.money_in} sign="in"/>
            <Line label="Out" value={dialog.ledger.money_out} sign="out"/>
            <Line label="Net" value={dialog.ledger.net} strong testId="register-event-net"/>
          </div>
        </Modal>
      )}
      {dialog?.kind === "void" && <VoidDialog entry={dialog.entry} busy={busy} onClose={() => setDialog(null)}
        onConfirm={async reason => { if (await run(() => api.voidEntry(dialog.entry.id, reason, token), "Entry struck out")) setDialog(null); }}/>}
      {dialog?.kind === "heads" && <HeadsDialog heads={heads} sections={sections} token={token} onChanged={loadSide} onClose={() => setDialog(null)}/>}
      {dialog?.kind === "sign" && (
        <SignDialog
          slot={dialog.slot}
          busy={busy}
          onClose={() => setDialog(null)}
          onConfirm={async pin => {
            if (await run(() => api.signSlot(day, dialog.slot.slot_id, pin, token), "Signed")) {
              setDialog(null);
            }
          }}
        />
      )}
    </PageShell>
  );
}

/** C6: pick the party booking this money is for, or make a new one in place. */
function EventPicker({ events, value, onChange, token, day, onCreated, testId }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const create = async () => {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const ev = await api.createEvent({ name: name.trim(), event_date: day }, token);
      if (onCreated) await onCreated();
      onChange(String(ev.id));
      setAdding(false); setName("");
      toast.success(`Event "${ev.name}" made`);
    } catch (error) {
      toast.error(api.failureText(error, "The event was not made."));
    } finally {
      setSaving(false);
    }
  };
  return (
    <label className="flex flex-col gap-tight">
      <span className="text-micro font-medium text-muted-foreground">Event (optional)</span>
      {adding ? (
        <span className="flex gap-snug">
          <Input className="h-11" placeholder="Party name" value={name} onChange={e => setName(e.target.value)} data-testid={`${testId}-new-name`}/>
          <Button variant="action" className="h-11" disabled={saving || !name.trim()} onClick={create} data-testid={`${testId}-create`}>Make</Button>
        </span>
      ) : (
        <select className="h-11 px-3 border rounded-md bg-card text-body" value={value}
          onChange={e => (e.target.value === "new" ? setAdding(true) : onChange(e.target.value))} data-testid={testId}>
          <option value="">None</option>
          {events.map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
          <option value="new">+ New event</option>
        </select>
      )}
    </label>
  );
}

function PayeeDialog({ payees, heads, events = [], token, day, onEventCreated, canCreate, busy, onClose, onConfirm }) {
  const [eventId, setEventIdLocal] = useState("");
  const [payeeId, setPayeeId] = useState("");
  const [newName, setNewName] = useState("");
  const [headId, setHeadIdLocal] = useState("");
  const [amount, setAmountLocal] = useState("");
  const [note, setNoteLocal] = useState("");
  const adding = payeeId === "new";
  const pick = value => {
    setPayeeId(value);
    const p = payees.find(x => String(x.id) === value);
    if (p?.default_head_id && heads.some(h => h.id === p.default_head_id)) setHeadIdLocal(String(p.default_head_id));
  };
  const ready = (adding ? newName.trim() : payeeId) && headId && num(amount) > 0;
  return (
    <Modal title="Pay someone without a bill" onClose={onClose} testId="register-payee-modal"
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button variant="action" disabled={busy || !ready} data-testid="register-payee-confirm"
          onClick={() => onConfirm({ payeeId: adding ? null : Number(payeeId), newName: newName.trim(), headId: Number(headId), amount, note, eventId })}>
          Pay {num(amount) > 0 ? inr(num(amount)) : ""}</Button></>}>
      <div className="flex flex-col gap-snug">
        <label className="flex flex-col gap-tight">
          <span className="text-micro font-medium text-muted-foreground">Who</span>
          <select className="h-11 px-3 border rounded-md bg-card text-body" value={payeeId} onChange={e => pick(e.target.value)} data-testid="register-payee-pick">
            <option value="">Pick…</option>
            {payees.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            {canCreate && <option value="new">+ Someone new</option>}
          </select>
        </label>
        {adding && (
          <Input className="h-11" placeholder="Their name" value={newName} onChange={e => setNewName(e.target.value)} data-testid="register-payee-new-name"/>
        )}
        <label className="flex flex-col gap-tight">
          <span className="text-micro font-medium text-muted-foreground">Head</span>
          <select className="h-11 px-3 border rounded-md bg-card text-body" value={headId} onChange={e => setHeadIdLocal(e.target.value)} data-testid="register-payee-head">
            <option value="">Pick…</option>
            {heads.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
          </select>
        </label>
        <Input className="h-11 text-right" inputMode="decimal" placeholder="Amount" value={amount} onChange={e => setAmountLocal(e.target.value)} data-testid="register-payee-amount"/>
        <Input className="h-11" placeholder="Note (what for)" value={note} onChange={e => setNoteLocal(e.target.value)} data-testid="register-payee-note"/>
        <EventPicker events={events} value={eventId} onChange={setEventIdLocal} token={token} day={day}
          onCreated={onEventCreated} testId="register-payee-event"/>
      </div>
    </Modal>
  );
}

function VoidDialog({ entry, busy, onClose, onConfirm }) {
  const [reason, setReason] = useState("");
  return (
    <Modal title="Strike out this entry?" onClose={onClose} testId="register-void-modal"
      footer={<><Button variant="outline" onClick={onClose}>Cancel</Button>
        <Button variant="destructive" disabled={busy || !reason.trim()} onClick={() => onConfirm(reason.trim())} data-testid="register-void-confirm">Strike out</Button></>}>
      <p className="text-body mb-2">{entry.head} · {inr(num(entry.amount))}. It stays on the day, struck out, with the reason.</p>
      <Input className="h-11" placeholder="Why" value={reason} onChange={e => setReason(e.target.value)} data-testid="register-void-reason"/>
    </Modal>
  );
}

const HEAD_KINDS = [
  { key: "expense", label: "Expense (P&L)" },
  { key: "transfer", label: "Transfer" },
  { key: "staff", label: "Staff payout" },
  { key: "income", label: "Other income" },
  { key: "vendor", label: "Vendor" },
];

function HeadsDialog({ heads, sections = DEFAULT_SECTIONS, token, onChanged, onClose }) {
  const [name, setName] = useState("");
  const [section, setSection] = useState("expense");
  const [kind, setKind] = useState("expense");
  const [busy, setBusy] = useState(false);
  const act = async (work, success) => {
    setBusy(true);
    try { await work(); toast.success(success); onChanged(); return true; }
    catch (error) { toast.error(api.failureText(error, "The head did not save.")); return false; }
    finally { setBusy(false); }
  };
  const save = (h, body, text = "Head saved") => act(() => api.updateHead(h.id, body, token), text);
  return (
    <Modal title="Register heads" size="wide"
      description="The lines cash goes in and out under, in the sheet's sections. A head used on a past day is switched off, never deleted."
      onClose={onClose} testId="register-heads-modal" footer={<Button variant="outline" onClick={onClose}>Done</Button>}>
      <div className="flex flex-col divide-y border rounded-md mb-3 max-h-[55vh] overflow-y-auto">
        {heads.map(h => (
          <div key={h.id} className="flex flex-wrap items-center gap-snug px-base py-snug" data-testid={`register-head-${h.id}`}>
            <Input className={`h-9 flex-1 min-w-[9rem] ${h.is_active ? "" : "line-through text-muted-foreground"}`} defaultValue={h.name} disabled={busy}
              onBlur={e => e.target.value.trim() && e.target.value.trim() !== h.name && save(h, { name: e.target.value.trim() }, "Renamed")}
              data-testid={`register-head-name-${h.id}`}/>
            <select className="h-9 px-2 border rounded-md bg-card text-meta" value={h.section || "other"} disabled={busy}
              onChange={e => save(h, { section: e.target.value, ...(e.target.value === "other" ? { direction: h.direction } : {}) })}
              data-testid={`register-head-section-${h.id}`}>
              {sections.map(s => <option key={s.key} value={s.key}>{t(s.label)}</option>)}
            </select>
            <select className="h-9 px-2 border rounded-md bg-card text-meta" value={h.kind || "expense"} disabled={busy}
              onChange={e => save(h, { kind: e.target.value })}
              data-testid={`register-head-kind-${h.id}`}>
              {HEAD_KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
            </select>
            {(h.section || "other") === "other" && (
              <select className="h-9 px-2 border rounded-md bg-card text-meta" value={h.direction} disabled={busy}
                onChange={e => save(h, { direction: e.target.value })}>
                <option value="in">Money in</option><option value="out">Money out</option>
              </select>
            )}
            {h.system_key && (
              <span className="text-micro font-mono bg-muted text-muted-foreground px-1.5 py-0.5 rounded" title={`System key: ${h.system_key}`}>
                System
              </span>
            )}
            <label className="flex items-center gap-tight text-meta"><input type="checkbox" checked={h.carries} disabled={busy}
              onChange={() => save(h, { carries: !h.carries })}/>Into tomorrow's opening</label>
            <label className="flex items-center gap-tight text-meta"><input type="checkbox" checked={h.needs_name} disabled={busy}
              onChange={() => save(h, { needs_name: !h.needs_name })}/>Asks who</label>
            {h.kind === "staff" && (
              <label className="flex items-center gap-tight text-meta"><input type="checkbox" checked={Boolean(h.is_advance)} disabled={busy}
                onChange={() => save(h, { is_advance: !h.is_advance })} data-testid={`register-head-advance-${h.id}`}/>Is an advance</label>
            )}
            <Button size="sm" variant="outline" disabled={busy} onClick={() => save(h, { is_active: !h.is_active }, h.is_active ? "Head switched off" : "Head switched on")}>
              {h.is_active ? "Switch off" : "Switch on"}
            </Button>
          </div>
        ))}
      </div>
      <div className="flex flex-wrap gap-snug">
        <Input className="h-11 flex-1 min-w-[10rem]" placeholder="New head, e.g. Gas cylinder" value={name} onChange={e => setName(e.target.value)} data-testid="register-head-name"/>
        <select className="h-11 px-2 border rounded-md bg-card text-body" value={section} onChange={e => setSection(e.target.value)} data-testid="register-head-section">
          {sections.map(s => <option key={s.key} value={s.key}>{t(s.label)}</option>)}
        </select>
        <select className="h-11 px-2 border rounded-md bg-card text-body" value={kind} onChange={e => setKind(e.target.value)} data-testid="register-head-kind">
          {HEAD_KINDS.map(k => <option key={k.key} value={k.key}>{k.label}</option>)}
        </select>
        <Button variant="action" className="h-11" disabled={busy || !name.trim()}
          onClick={async () => { if (await act(() => api.createHead({ name: name.trim(), section, kind, ...(section === "other" ? { direction: "out" } : {}) }, token), "Head added")) setName(""); }} data-testid="register-head-add">Add</Button>
      </div>
    </Modal>
  );
}

function SignDialog({ slot, busy, onClose, onConfirm }) {
  const [pin, setPin] = useState("");
  return (
    <Modal
      title={`Sign ${slot.slot_label}`}
      onClose={onClose}
      testId="register-sign-modal"
      footer={
        <>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="action" disabled={busy || !pin.trim()} onClick={() => onConfirm(pin.trim())} data-testid="register-sign-confirm">Sign</Button>
        </>
      }
    >
      <p className="text-body mb-2">Enter your employee PIN to sign the {slot.slot_label} slot.</p>
      <Input
        type="password"
        className="h-11"
        placeholder="PIN"
        value={pin}
        onChange={e => setPin(e.target.value)}
        onKeyDown={e => e.key === "Enter" && pin.trim() && onConfirm(pin.trim())}
        data-testid="register-sign-pin"
      />
    </Modal>
  );
}
